<script lang="ts">
    import type { UploadItem } from '../services/uploadService';
    import Modal from './Modal.svelte';
    import { SvelteMap } from 'svelte/reactivity';

    interface MediaItem {
        id: number;
        url: string;
        caption: string;
        mime_type: string;
    }

    interface Props {
        label: string;
        description?: string;
        accept?: string;
        existingItems: MediaItem[];
        uploadItems: UploadItem[];
        captions: Record<number, string>;
        removedIds: number[];
        onfilesadded: (files: File[]) => void;
        onexistingremove: (id: number) => void;
        onuploadremove: (id: string) => void;
        onlightbox?: (images: { id?: number; src: string; alt?: string; caption?: string }[], index: number) => void;
        onexistingreorder: (ids: number[]) => void;
        onuploadreorder: (items: UploadItem[]) => void;
    }

    let {
        label,
        description = '',
        accept = '*/*',
        existingItems,
        uploadItems,
        captions,
        removedIds,
        onfilesadded,
        onexistingremove,
        onuploadremove,
        onlightbox,
        onexistingreorder,
        onuploadreorder,
    }: Props = $props();

    const visibleItems = $derived(
        existingItems.filter(item => !removedIds.includes(item.id))
    );

    const totalItemCount = $derived(visibleItems.length + uploadItems.length);

    const visibleImages = $derived(
        visibleItems.filter(item => item.mime_type.startsWith('image/')).map(item => ({
            id: item.id,
            src: item.url,
            alt: captions[item.id] ?? item.caption,
            caption: captions[item.id] ?? item.caption,
        }))
    );

    // Patient info warning — shown once per component on first file add
    let hasAcknowledged = $state(false);
    let pendingFiles = $state<File[]>([]);
    let warningChecked = $state(false);
    let showWarning = $state(false);

    function addFiles(files: File[]) {
        createPreviews(files);
        if (hasAcknowledged) {
            onfilesadded(files);
        } else {
            pendingFiles = [...pendingFiles, ...files];
            showWarning = true;
        }
    }

    function confirmWarning() {
        hasAcknowledged = true;
        showWarning = false;
        warningChecked = false;
        const files = pendingFiles;
        pendingFiles = [];
        onfilesadded(files);
    }

    function cancelWarning() {
        showWarning = false;
        for (const file of pendingFiles) revokePreview(file);
        pendingFiles = [];
        warningChecked = false;
    }

    // Handle modal dismissed via backdrop click or escape key
    $effect(() => {
        if (!showWarning && pendingFiles.length > 0) {
            cancelWarning();
        }
    });

    // Preview URLs for new image files — created eagerly, not during render
    let previewUrls = new SvelteMap<File, string>();

    function createPreviews(files: File[]) {
        for (const file of files) {
            if (file.type.startsWith('image/') && !previewUrls.has(file)) {
                previewUrls.set(file, URL.createObjectURL(file));
            }
        }
    }

    function revokePreview(file: File) {
        const url = previewUrls.get(file);
        if (url) {
            URL.revokeObjectURL(url);
            previewUrls.delete(file);
        }
    }

    function handleFileChange(e: Event) {
        const input = e.target as HTMLInputElement;
        if (input.files) {
            addFiles(Array.from(input.files));
            input.value = '';
        }
    }

    function handleUploadRemove(item: UploadItem) {
        revokePreview(item.file);
        item.abort?.();
        onuploadremove(item.id);
    }

    // File drop zone state
    let fileDragOver = $state(false);

    function handleFileDrop(e: DragEvent) {
        e.preventDefault();
        fileDragOver = false;
        if (!e.dataTransfer?.files?.length) return;
        addFiles(Array.from(e.dataTransfer.files));
    }

    function handleFileDragOver(e: DragEvent) {
        // Only show drop zone for external files, not internal reorder drags
        if (dragGroup) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        fileDragOver = true;
    }

    function handleFileDragLeave(e: DragEvent) {
        // Only reset if leaving the drop zone entirely (not entering a child)
        if (e.currentTarget && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
            fileDragOver = false;
        }
    }

    // Drag state
    let dragGroup: 'existing' | 'new' | null = $state(null);
    let dragIndex: number | null = $state(null);
    let dropTarget: number | null = $state(null);

    function onDragStart(group: 'existing' | 'new', index: number, e: DragEvent) {
        dragGroup = group;
        dragIndex = index;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', '');
        }
    }

    function onDragOver(group: 'existing' | 'new', index: number, e: DragEvent) {
        if (dragGroup !== group) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        dropTarget = index;
    }

    function onDrop(group: 'existing' | 'new', index: number, e: DragEvent) {
        e.preventDefault();
        if (dragGroup !== group || dragIndex === null || dragIndex === index) {
            resetDrag();
            return;
        }

        if (group === 'existing') {
            const ids = visibleItems.map(item => item.id);
            const [moved] = ids.splice(dragIndex, 1);
            ids.splice(index, 0, moved);
            onexistingreorder(ids);
        } else {
            const items = [...uploadItems];
            const [movedItem] = items.splice(dragIndex, 1);
            items.splice(index, 0, movedItem);
            onuploadreorder(items);
        }

        resetDrag();
    }

    function onDragEnd() {
        resetDrag();
    }

    function resetDrag() {
        dragGroup = null;
        dragIndex = null;
        dropTarget = null;
    }

    function statusColor(status: UploadItem['status']): string {
        switch (status) {
            case 'uploading': return 'text-cyan-400';
            case 'done': return 'text-green-400';
            case 'error': return 'text-red-400';
            default: return 'text-gray-400';
        }
    }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="rounded-lg border-2 border-dashed transition-colors p-4 {fileDragOver ? 'border-cyan-400 bg-cyan-950' : 'border-transparent'}"
    ondrop={handleFileDrop}
    ondragover={handleFileDragOver}
    ondragleave={handleFileDragLeave}
>
    <label class="block text-sm font-medium text-gray-300 mb-2">{label}</label>
    {#if description}
        <p class="text-sm text-gray-500 mb-3">{description}</p>
    {/if}

    {#if visibleItems.length || uploadItems.length}
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {#each visibleItems as item, i (item.id)}
                <figure
                    class="relative group transition-opacity {dragGroup === 'existing' && dropTarget === i && dragIndex !== i ? 'ring-2 ring-cyan-500 ring-dashed rounded-lg' : ''} {dragGroup === 'existing' && dragIndex === i ? 'opacity-40' : ''}"
                    draggable={totalItemCount > 1}
                    ondragstart={(e: DragEvent) => onDragStart('existing', i, e)}
                    ondragover={(e: DragEvent) => onDragOver('existing', i, e)}
                    ondrop={(e: DragEvent) => onDrop('existing', i, e)}
                    ondragend={onDragEnd}
                    ondragleave={() => { if (dropTarget === i) dropTarget = null; }}
                >
                    {#if item.mime_type.startsWith('image/')}
                        <button
                            type="button"
                            onclick={onlightbox ? () => onlightbox(visibleImages, visibleImages.findIndex(img => img.src === item.url)) : undefined}
                            class="w-full aspect-[4/3] bg-gray-800 border-2 border-gray-700 rounded-lg cursor-zoom-in flex items-center justify-center overflow-hidden hover:border-gray-600 transition-colors"
                        >
                            <img src={item.url} alt={captions[item.id] ?? item.caption} class="max-w-full max-h-full object-contain" />
                        </button>
                    {:else}
                        <div class="w-full aspect-[4/3] bg-gray-800 border-2 border-gray-700 rounded-lg flex items-center justify-center">
                            <span class="text-sm text-gray-500">{item.mime_type.startsWith('video/') ? 'Video' : 'File'}</span>
                        </div>
                    {/if}
                    <button
                        type="button"
                        onclick={() => onexistingremove(item.id)}
                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 drop-shadow-md"
                    >
                        <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path opacity=".4" fill="currentColor" d="M32 96l384 0-21.4 370.8c-1.5 25.4-22.5 45.2-47.9 45.2l-245.4 0c-25.4 0-46.5-19.8-47.9-45.2L32 96z"/><path fill="currentColor" d="M167.1-16c-13.8 0-26 8.8-30.4 21.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16z"/></svg>
                    </button>
                    {#if totalItemCount > 1}
                        <div class="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-white bg-black/50 rounded-full w-6 h-6 flex items-center justify-center backdrop-blur-sm" aria-label="Drag to reorder">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 320 512"><path d="M128 64A64 64 0 1 0 0 64 64 64 0 1 0 128 64zm0 192a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zM0 448c0 35.3 28.7 64 64 64s64-28.7 64-64-28.7-64-64-64-64 28.7-64 64zM320 64a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zM192 256a64 64 0 1 0 128 0 64 64 0 1 0 -128 0zM320 448c0-35.3-28.7-64-64-64s-64 28.7-64 64 28.7 64 64 64 64-28.7 64-64z"/></svg>
                        </div>
                    {/if}
                    <input
                        type="text"
                        value={captions[item.id] ?? ''}
                        oninput={(e: Event) => { captions[item.id] = (e.target as HTMLInputElement).value; }}
                        placeholder="Caption (optional)"
                        class="mt-2 w-full text-sm text-gray-100 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 placeholder-gray-500 focus:border-cyan-600 focus:outline-none"
                    />
                </figure>
            {/each}

            {#each uploadItems as item, i (item.id)}
                <figure
                    class="relative group transition-opacity {dragGroup === 'new' && dropTarget === i && dragIndex !== i ? 'ring-2 ring-cyan-500 ring-dashed rounded-lg' : ''} {dragGroup === 'new' && dragIndex === i ? 'opacity-40' : ''}"
                    draggable={totalItemCount > 1}
                    ondragstart={(e: DragEvent) => onDragStart('new', i, e)}
                    ondragover={(e: DragEvent) => onDragOver('new', i, e)}
                    ondrop={(e: DragEvent) => onDrop('new', i, e)}
                    ondragend={onDragEnd}
                    ondragleave={() => { if (dropTarget === i) dropTarget = null; }}
                >
                    <div class="relative w-full aspect-[4/3] bg-gray-800 border-2 rounded-lg flex items-center justify-center overflow-hidden {item.status === 'error' ? 'border-red-500' : 'border-gray-700'}">
                        {#if item.file.type.startsWith('image/') && previewUrls.get(item.file)}
                            <img src={previewUrls.get(item.file)} alt={item.file.name} class="max-w-full max-h-full object-contain" />
                        {:else}
                            <span class="text-sm text-gray-500">{item.file.type.startsWith('video/') ? 'Video' : item.file.type === 'application/pdf' ? 'PDF' : item.file.name}</span>
                        {/if}

                        <!-- Progress overlay -->
                        {#if item.status === 'uploading' || item.status === 'pending'}
                            <div class="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <svg class="w-14 h-14 -rotate-90" viewBox="0 0 48 48">
                                    <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" stroke-width="3" class="text-gray-700" />
                                    <circle
                                        cx="24" cy="24" r="20" fill="none" stroke="currentColor" stroke-width="3"
                                        class="text-cyan-400 transition-all duration-300"
                                        stroke-dasharray={2 * Math.PI * 20}
                                        stroke-dashoffset={2 * Math.PI * 20 * (1 - (item.progress ?? 0) / 100)}
                                        stroke-linecap="round"
                                    />
                                </svg>
                                <span class="absolute text-xs font-medium text-white">{item.progress ?? 0}%</span>
                            </div>
                        {/if}

                        <!-- Error overlay -->
                        {#if item.status === 'error'}
                            <div class="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 p-2">
                                <span class="text-xs text-red-400 text-center">{item.error ?? 'Upload failed'}</span>
                                {#if item.retry}
                                    <button
                                        type="button"
                                        onclick={() => item.retry?.()}
                                        class="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                                    >
                                        Retry
                                    </button>
                                {/if}
                            </div>
                        {/if}

                        <!-- Done checkmark -->
                        {#if item.status === 'done'}
                            <div class="absolute bottom-2 left-2 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                &#10003;
                            </div>
                        {/if}
                    </div>
                    <button
                        type="button"
                        onclick={() => handleUploadRemove(item)}
                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 drop-shadow-md"
                    >
                        <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path opacity=".4" fill="currentColor" d="M32 96l384 0-21.4 370.8c-1.5 25.4-22.5 45.2-47.9 45.2l-245.4 0c-25.4 0-46.5-19.8-47.9-45.2L32 96z"/><path fill="currentColor" d="M167.1-16c-13.8 0-26 8.8-30.4 21.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16z"/></svg>
                    </button>
                    {#if totalItemCount > 1}
                        <div class="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-white bg-black/50 rounded-full w-6 h-6 flex items-center justify-center backdrop-blur-sm" aria-label="Drag to reorder">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 320 512"><path d="M128 64A64 64 0 1 0 0 64 64 64 0 1 0 128 64zm0 192a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zM0 448c0 35.3 28.7 64 64 64s64-28.7 64-64-28.7-64-64-64-64 28.7-64 64zM320 64a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zM192 256a64 64 0 1 0 128 0 64 64 0 1 0 -128 0zM320 448c0-35.3-28.7-64-64-64s-64 28.7-64 64 28.7 64 64 64 64-28.7 64-64z"/></svg>
                        </div>
                    {/if}
                    <input
                        bind:value={item.caption}
                        type="text"
                        placeholder="Caption (optional)"
                        class="mt-2 w-full text-sm text-gray-100 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 placeholder-gray-500 focus:border-cyan-600 focus:outline-none"
                    />
                </figure>
            {/each}
        </div>
    {/if}

    <div class="rounded-lg border-2 border-dashed border-gray-700 p-4 text-center transition-colors {fileDragOver ? 'border-cyan-400 bg-cyan-950' : ''}">
        {#if fileDragOver}
            <p class="text-sm text-cyan-400 font-medium">Drop files to add them</p>
        {:else}
            <p class="text-sm text-gray-500 mb-2">Drag & drop files here, or</p>
            <label class="inline-block cursor-pointer rounded bg-cyan-950 px-4 py-2 text-sm font-medium text-cyan-400 hover:bg-cyan-900">
                Browse files
                <input
                    type="file"
                    {accept}
                    multiple
                    onchange={handleFileChange}
                    class="hidden"
                />
            </label>
        {/if}
    </div>
</div>

<Modal bind:open={showWarning} title="Patient Information Warning" panelClass="warning-glow border border-amber-500/60">
    <p class="text-sm text-gray-400 mb-4">You are responsible for ensuring all uploaded media has been properly redacted of any patient-identifying information (names, dates of birth, medical record numbers, etc.).</p>
    <label class="flex items-start gap-2 text-sm text-gray-300 cursor-pointer mb-5">
        <input type="checkbox" bind:checked={warningChecked} class="mt-0.5 accent-amber-500" />
        <span>I confirm that all files I am uploading have been redacted of patient information.</span>
    </label>
    <div class="flex justify-end gap-3">
        <button
            type="button"
            onclick={cancelWarning}
            class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-gray-700"
        >
            Cancel
        </button>
        <button
            type="button"
            onclick={confirmWarning}
            disabled={!warningChecked}
            class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            Continue with upload
        </button>
    </div>
</Modal>

<style>
    @keyframes warning-glow {
        0%, 100% {
            box-shadow: 0 0 40px 20px rgba(245, 158, 11, 0.3);
        }
        50% {
            box-shadow: 0 0 60px 30px rgba(245, 158, 11, 0.6);
        }
    }

    :global(.warning-glow) {
        animation: warning-glow 2s ease-in-out infinite;
    }
</style>
